import {
    Flex,
    Icon,
    PriorityIcon,
    Text,
    Separator,
    DropdownV2,
    TriggerWrapperV2,
    DropdownSection,
    ListItem
} from '@priority-software/priority-style-react';
import React, {forwardRef, useEffect, useState, useCallback, useRef} from 'react';
import classNames from 'classnames';

import {helpPopoverRef} from '../../../_shared/Help/constants';
import texts from '../texts';
import styles from '../styles/MacroMenu.module.scss';
import {isRTL} from '../../../Localization';
import {observer} from 'mobx-react';
import {ItemsList} from '../../../components/Common/ItemsList';
import {withHover} from '../../../components/Common/HOC/withHover';
import {showConfirmMessage} from '../../../_shared/Messages/actions/MessageActions';
import {macroDlgRef} from './MacroDialog';
import {formsStore} from '../../Form/stores/FormsStore';
import {
    getMacroActionsFromServer,
    getMacroIdByKey,
    isRecursiveByActions,
    onDeleteMacro,
    startMacro,
    stopRecording
} from '../actions/macroActions';
import {setIsReactPopupOpen} from '../../../_shared/GWTBridge/invoke/netForm';
import {iSearchDropdownCloseKey} from '../../FieldSearch/utils/keys';
import {macroFooterRef} from '../constants';
import {sendMonitor} from '../../../utils/monitoring';
import {useKeyboardNavigation} from '../../../components/Common/FieldSearch/hooks/useKeyboardNavigation';
import {returnFocus} from '../../../_shared/GWTBridge/invoke/genPage';
import {getMacroNameById} from '../actions/macroActions';
import {useHover} from '../../../hooks/useHover';
import {useEventListener} from '../../../hooks/useEventListener';
import {fetchSysLangs} from '../../../Localization/languages';
import {isItemVisible} from '../../FormHeader/utils/isItemVisible';
import {ButtonTrigger} from './MacroMenuTrigger';

const SavedMacroComp = withHover((props: any) => {
    const {
        item: macro,
        onDelete,
        onEdit,
        onActivateMacro,
        isHovering,
        index,
        itemRef,
        isFocused
    } = props;

    return (
        <ListItem
            componentClasses={index !== 0 && styles.separator}
            isActive={false}
            isFocused={isFocused}
            disabled={
                formsStore.currentRecordingMacro?.macro === props.item.macro &&
                props.item.isRecursive
            }
            ref={itemRef}
            onClick={() => onActivateMacro(macro.macro)}
        >
            <Flex componentClasses={styles.submenuItem} justifyContent="space-between">
                <Text wrap={false} componentClasses={styles.macroName} skin="subtitle-2">
                    {macro.origtitle}
                </Text>
                {isHovering && macro.maydelete === '1' ? (
                    <Flex componentClasses={styles.hoverActions} justifyContent="flex-end">
                        <Icon
                            onClick={e => onEdit(e, macro)}
                            iconColor="primary-500"
                            componentClasses={styles.hoverIcon}
                            skin="standart"
                            icon={PriorityIcon.editFill}
                        />
                        <Separator height={25} />
                        <Icon
                            skin="standart"
                            icon={PriorityIcon.delete}
                            onClick={e => {
                                e.stopPropagation();
                                onDelete(macro.macro);
                            }}
                            iconColor="primary-500"
                            componentClasses={styles.hoverIcon}
                        />
                    </Flex>
                ) : (
                    macro.key && (
                        <Text
                            componentClasses={styles.shortKey}
                            wrap={false}
                            textColor="secondary-400"
                            skin="subtitle-2"
                        >
                            <span>[</span>
                            {`ALT + ${macro.key}`}
                            <span>]</span>
                        </Text>
                    )
                )}
            </Flex>
        </ListItem>
    );
});

const SavedMacro = forwardRef((props, ref) => <SavedMacroComp {...props} itemRef={ref} />);

export const MacroSubMenu = observer(
    ({
        items,
        isOpen,
        onEdit,
        onDeleteMacro,
        onStartRecording,
        onStopRecording,
        onActivateMacro,
        onHelp,
        onCloseSubMenu,
        isHovering,
        useBridgeAPI
    }) => {
        const isRecording = formsStore.currentRecordingMacro != null;
        const listLength = items?.length;

        const handleSelecetFocusedItem = e => {
            if (focusedIndex === listLength) {
                isRecording ? onStopRecording() : onStartRecording();
            } else {
                onActivateMacro(items[focusedIndex]?.macro);
            }
        };
        const {focusedIndex, handleKeyboardNavigation} = useKeyboardNavigation({
            initialFocusedIndex: 0,
            listLength,
            pageSize: 6,
            onSelect: handleSelecetFocusedItem,
            onNavigateLeft: onCloseSubMenu
        });

        return (
            <Flex>
                <Flex justifyContent="flex-start">
                    <Text skin={useBridgeAPI ? 'paragraph-1' : 'subtitle-2'}>
                        {texts.menu_macro}
                    </Text>
                    {isHovering && (
                        <Icon
                            icon={PriorityIcon.help}
                            componentClasses={styles.macroIconMarginSurfix}
                            onClick={e => onHelp(e, texts.menu_macroHelp)}
                        />
                    )}
                </Flex>
                <DropdownV2
                    contentComponentClasses={styles.dropdownSection}
                    isOpen={isOpen}
                    trigger={
                        <TriggerWrapperV2 isOpen={isOpen}>
                            <Icon
                                componentClasses={isOpen ? styles.menuArrow : ''}
                                icon={isRTL() ? PriorityIcon.arrowLeft : PriorityIcon.arrowRight}
                            />
                        </TriggerWrapperV2>
                    }
                    onKeyDown={handleKeyboardNavigation}
                    onClose={onCloseSubMenu}
                >
                    <div onClick={e => e.stopPropagation()}>
                        <DropdownSection>
                            <ItemsList
                                autoHeight
                                autoHeightMax={310}
                                isItemSelected={() => {}}
                                items={items}
                                focusedIndex={focusedIndex}
                                renderItem={SavedMacro}
                                componentClasses={styles.scrollWrapper}
                                itemProps={{
                                    onDelete: onDeleteMacro,
                                    onActivateMacro: onActivateMacro,
                                    onEdit: onEdit
                                }}
                            />
                            <ListItem
                                onClick={isRecording ? onStopRecording : onStartRecording}
                                isActive={false}
                                isFocused={focusedIndex === items.length}
                            >
                                <Flex justifyContent="flex-start">
                                    <Icon
                                        icon={
                                            isRecording
                                                ? PriorityIcon.stopRecording
                                                : PriorityIcon.play
                                        }
                                        componentClasses={classNames(styles.iconMarginPrefix, {
                                            [styles.stopIcon]: isRecording
                                        })}
                                    />
                                    <Text skin="caption-1" componentClasses={styles.recordItem}>
                                        {isRecording
                                            ? texts.footer_stopTooltip
                                            : texts.menu_recordMacro}
                                    </Text>
                                </Flex>
                            </ListItem>
                        </DropdownSection>
                    </div>
                </DropdownV2>
            </Flex>
        );
    }
);

export const MacroMenu = observer(({settingsItems = null, macroSubmenu = true}) => {
    const [open, setOpen] = useState(false);
    const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
    const {setRef, isHovering} = useHover();

    const macros = formsStore.currentForm?.macros;
    const listLength = settingsItems?.length;

    const preventClose = useRef(false);

    const handleSelectFocusedIndex = e => {
        if (focusedIndex === 0) {
            setIsSubmenuOpen(true);
        } else {
            onItemClick(settingsItems[focusedIndex - 1]);
        }
    };

    const handleNavigateRight = () => {
        if (focusedIndex === 0) {
            handlToggleSubMenuDropdown();
        }
    };
    const {focusedIndex, handleKeyboardNavigation, setFocusedIndex} = useKeyboardNavigation({
        initialFocusedIndex: 0,
        listLength,
        pageSize: 5,
        onSelect: handleSelectFocusedIndex,
        onNavigateRight: handleNavigateRight
    });

    useEffect(() => {
        setFocusedIndex(0);
    }, [setFocusedIndex]);

    const handleClose = useCallback(
        (shouldeturnFocus?) => {
            if (!preventClose.current) {
                setOpen(false);
                setIsSubmenuOpen(false);
                shouldeturnFocus && returnFocus();
            }
            preventClose.current = false;
            setIsReactPopupOpen(false);
        },
        [preventClose]
    );

    const handleActivateMacro = useCallback(
        macroID => {
            const macroName = getMacroNameById(macroID);
            sendMonitor(`PlayMacro"${macroName}"`);
            handleClose();
            startMacro(macroID);
        },
        [handleClose]
    );

    const handleKeyDown = useCallback(
        e => {
            //not use "e.key" for macOS support
            const num = e.code?.replace('Digit', '').replace('Numpad', '');
            if (e.altKey && Number(num) <= 9) {
                const macroID = getMacroIdByKey(num);
                if (macroID) {
                    handleActivateMacro(macroID);
                }
            }
        },
        [handleActivateMacro]
    );

    useEventListener('keydown', handleKeyDown, window, true);

    const handlToggleSubMenuDropdown = () => {
        setIsSubmenuOpen(prevState => !prevState);
    };

    const handleDeleteMacro = macroID => {
        showConfirmMessage({
            title: texts.menu_deleteMessage,
            onConfirm: () => onDeleteMacro(macroID),
            confirmButtonText: texts.menu_deleteYes,
            cancleButtonText: texts.menu_deleteNo,
            defaultButton: 0
        });
        preventClose.current = true;
    };

    const handleStartRecording = () => {
        sendMonitor('RecordMacro');
        handleClose();
        macroDlgRef.current.handleShowDialog();
    };

    const handleEditMacro = async (e, macro) => {
        e.stopPropagation();

        const macroActions = await getMacroActionsFromServer(macro.macro);
        const isRecursive = isRecursiveByActions(macroActions, macro.macro);
        macroDlgRef.current.handleShowDialog({
            id: macro.macro,
            key: macro.key,
            name: macro.origtitle,
            isRecursive,
            macroActions
        });
        handleClose();
    };

    const handleShowHelp = (e, text) => {
        e.stopPropagation();
        helpPopoverRef.current.showHelpPopover({title: 'Help', content: text, anchor: e.target});
        preventClose.current = true;
    };

    const handleOpen = () => {
        setOpen(true);
        setIsReactPopupOpen(true);
    };

    const toggleOpen = () => {
        open ? handleClose() : handleOpen();
    };

    const handleStopRecording = () => {
        stopRecording();
        macroFooterRef?.current?.hideFooter();
    };

    const onItemClick = async item => {
        await fetchSysLangs();
        item.action && item.action(item.command, item.value);
        sendMonitor(`setting item click - ${item.title}`);
        handleClose(true);
    };

    const curretFocusedIndex = !isSubmenuOpen ? focusedIndex : -1;

    return (
        <DropdownV2
            contentComponentClasses={styles.dropdownSection}
            onClose={() => handleClose(true)}
            width="fullWidth"
            trigger={<ButtonTrigger isOpen={open} onClick={toggleOpen} />}
            isOpen={open}
            isCloseKeyCallback={iSearchDropdownCloseKey}
            onKeyDown={e => !isSubmenuOpen && handleKeyboardNavigation(e)}
        >
            <DropdownSection>
                {macroSubmenu && (
                    <ListItem
                        onClick={handlToggleSubMenuDropdown}
                        isActive={isSubmenuOpen}
                        isFocused={curretFocusedIndex === 0}
                        ref={setRef}
                        componentClasses={'ignore-react-onclickoutside'}
                    >
                        <MacroSubMenu
                            onHelp={handleShowHelp}
                            isOpen={isSubmenuOpen}
                            items={macros}
                            onDeleteMacro={handleDeleteMacro}
                            onActivateMacro={handleActivateMacro}
                            onStartRecording={handleStartRecording}
                            onStopRecording={handleStopRecording}
                            onEdit={handleEditMacro}
                            onCloseSubMenu={handlToggleSubMenuDropdown}
                            isHovering={isHovering}
                            useBridgeAPI={false}
                        />
                    </ListItem>
                )}
                {settingsItems?.map(
                    (item, index) =>
                        isItemVisible(item, item.isPopup) && (
                            <MacroItemWithHover
                                item={item}
                                key={item.title + item.index}
                                onItemClick={onItemClick}
                                index={index}
                                curretFocusedIndex={curretFocusedIndex}
                                handleShowHelp={handleShowHelp}
                            />
                        )
                )}
            </DropdownSection>
        </DropdownV2>
    );
});

const MacroItemWithHover = ({item, onItemClick, index, curretFocusedIndex, handleShowHelp}) => {
    const {setRef, isHovering} = useHover();

    return (
        <ListItem
            key={item.title}
            ref={setRef}
            onClick={() => onItemClick(item)}
            isFocused={curretFocusedIndex === index + 1}
            isActive={false}
        >
            <Flex justifyContent="flex-start">
                <Text skin="subtitle-2">{item.title}</Text>
                {isHovering && (
                    <Icon
                        icon={PriorityIcon.help}
                        componentClasses={styles.macroIconMarginSurfix}
                        onClick={e => handleShowHelp(e, item.desc)}
                    />
                )}
            </Flex>
        </ListItem>
    );
};
